import { useState, useRef } from 'react';
import { X, Upload, Camera, Trash2 } from 'lucide-react';
import { colors } from '../styles/colors';
import imageCompression from 'browser-image-compression';

function UpdatePhotoModal({ isOpen, onClose, currentPhoto, onPhotoUpdate, userName }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentPhoto);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier le type de fichier
    if (!file.type.startsWith('image/')) {
      setError("Veuillez sélectionner une image (JPG, PNG, GIF)");
      return;
    }

    // Options de compression
    const options = {
      maxSizeMB: 0.1,           // 👉 0.1 MB = 100 KB
      maxWidthOrHeight: 600,    // réduit la résolution
      useWebWorker: true
    };

    try {
      const compressedFile = await imageCompression(file, options);

      setSelectedFile(compressedFile);

      // Préparer la preview
      const previewReader = new FileReader();
      previewReader.onloadend = () => {
        setPreviewUrl(previewReader.result);
      };
      previewReader.readAsDataURL(compressedFile);
    } catch (err) {
      console.error("Erreur compression:", err);
      setError("Impossible de compresser l'image");
    }
  };

const handleUpload = async () => {
  if (!selectedFile) {
    setError("Veuillez sélectionner une image");
    return;
  }

  setIsUploading(true);

  const reader = new FileReader();
  reader.onloadend = async () => {
    const base64 = reader.result;
    await onPhotoUpdate(base64);
    handleClose();
  };
  reader.readAsDataURL(selectedFile);
};

  const handleRemovePhoto = async () => {
    setIsUploading(true);
    setError('');

    try {
      await onPhotoUpdate(null);
      handleClose();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Erreur lors de la suppression de la photo');
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(currentPhoto);
    setError('');
    setIsUploading(false);
    onClose();
  };

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          fontFamily: '"Noto Sans", sans-serif'
        }}
        onClick={handleClose}
      >
        {/* Modal */}
        <div
          style={{
            backgroundColor: colors.text.white,
            borderRadius: '0.75rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{
            padding: '1.5rem',
            borderBottom: `1px solid ${colors.secondary.lightGray}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '1.5rem',
              fontWeight: '600',
              color: colors.primary.gray
            }}>
              Photo de profil
            </h2>
            <button
              onClick={handleClose}
              disabled={isUploading}
              style={{
                background: 'none',
                border: 'none',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                padding: '0.5rem',
                color: colors.secondary.mediumGray,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.375rem',
                transition: 'background-color 200ms'
              }}
              onMouseEnter={(e) => {
                if (!isUploading) e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
              }}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '1.5rem' }}>
            <p style={{
              margin: '0 0 1.5rem 0',
              fontSize: '0.875rem',
              color: colors.secondary.mediumGray
            }}>
              {userName ? `Photo de ${userName}` : 'Modifier votre photo de profil'}
            </p>

            {/* Preview */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '1.5rem'
            }}>
              <div style={{
                width: '150px',
                height: '150px',
                borderRadius: '50%',
                backgroundColor: previewUrl ? 'transparent' : colors.secondary.lightTeal,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                border: `3px solid ${colors.secondary.lightGray}`
              }}>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <Camera size={48} color={colors.primary.teal} />
                )}
              </div>
            </div>

            {/* File input (hidden) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: colors.secondary.lightBlue,
                  color: colors.text.white,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  transition: 'all 200ms',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  opacity: isUploading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isUploading) e.currentTarget.style.backgroundColor = '#2563eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.secondary.lightBlue;
                }}
              >
                <Upload size={20} />
                Choisir une photo
              </button>

              {(currentPhoto || previewUrl) && (
                <button
                  onClick={handleRemovePhoto}
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    backgroundColor: colors.text.white,
                    color: '#dc2626',
                    border: '1px solid #dc2626',
                    borderRadius: '0.5rem',
                    fontSize: '1rem',
                    fontWeight: '500',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    transition: 'all 200ms',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isUploading ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading) {
                      e.currentTarget.style.backgroundColor = '#fef2f2';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = colors.text.white;
                  }}
                >
                  <Trash2 size={20} />
                  Supprimer la photo
                </button>
              )}
            </div>

            {/* Message d'erreur */}
            {error && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '0.5rem',
                color: '#991b1b',
                fontSize: '0.875rem'
              }}>
                {error}
              </div>
            )}

            {/* Info */}
            <p style={{
              marginTop: '1rem',
              fontSize: '0.75rem',
              color: colors.secondary.mediumGray,
              textAlign: 'center'
            }}>
              Formats acceptés : JPG, PNG, GIF • Taille max : 5 MB
            </p>
          </div>

          {/* Footer */}
          <div style={{
            padding: '1.5rem',
            borderTop: `1px solid ${colors.secondary.lightGray}`,
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={handleClose}
              disabled={isUploading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: colors.text.white,
                color: colors.primary.gray,
                border: `1px solid ${colors.secondary.lightGray}`,
                borderRadius: '0.5rem',
                fontSize: '1rem',
                fontWeight: '500',
                cursor: isUploading ? 'not-allowed' : 'pointer',
                transition: 'all 200ms',
                opacity: isUploading ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isUploading) e.currentTarget.style.backgroundColor = colors.secondary.lightGray;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = colors.text.white;
              }}
            >
              Annuler
            </button>

            {selectedFile && (
              <button
                onClick={handleUpload}
                disabled={isUploading}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: colors.primary.teal,
                  color: colors.text.white,
                  border: 'none',
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '500',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  transition: 'all 200ms',
                  opacity: isUploading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isUploading) e.currentTarget.style.backgroundColor = '#005f6a';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = colors.primary.teal;
                }}
              >
                {isUploading ? 'Envoi en cours...' : 'Enregistrer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default UpdatePhotoModal;
